import {
  useGetTaskIdsQuery,
  useGetTasksQuery,
} from "@/modules/capture/services/captureApi";
import { useActiveProject } from "@/modules/project/hooks/useActiveProject";
import { useGetProjectLabelsQuery } from "@/modules/project/services/projectApi";
import { Alert, AlertDescription, AlertTitle } from "@/modules/shadcn/ui/alert";
import { Button } from "@/modules/shadcn/ui/button";
import { Input } from "@/modules/shadcn/ui/input";
import { Label } from "@/modules/shadcn/ui/label";
import { NumberInput } from "@/modules/shadcn/ui/number-input";
import {
  isConversionTargetSupported,
  ModelBackendEnum,
  ModelOutputTypeEnum,
  ModelQuantizationEnum,
  ModelRegionEnum,
  Label as ProjectLabel,
  ProjectTypeEnum,
  SuggestHyperparamsRequest,
} from "@repo/schema";
import { AlertTriangle, TriangleAlert } from "lucide-react";
import type { TFunction } from "i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import {
  AppliedAutoSuggestion,
  useAutoSuggestion,
} from "../../hooks/useAutoSuggestion";
import { useTrainingQuota } from "../../hooks/useTrainingQuota";
import {
  useCreateModelMutation,
  useDatasetStatsMutation,
  useGetModelsQuery,
} from "../../services";
import { AdvancedSettings } from "../AdvancedSettings";
import { getHyperparamsPresets } from "../AdvancedSettings/presets";
import { AiSuggestionNotes } from "../AiSuggestionNotes";
import { AppliedAugmentation } from "../AugmentationSettings/augmentationTypes";
import { DatasetSplit, DatasetSplitSettings } from "../DatasetSplitSettings";
import { ModelLayout } from "../ModelLayout/ModelLayout";
import { ModelTypeSettings } from "../ModelTypeSettings";
import {
  MAX_VISIBLE_THUMBNAILS,
  SourceImagesSettings,
} from "../SourceImagesSettings";
import { TaskSelectionDialog } from "../TaskSelectionDialog";
import { SuggestionLoadingScreen } from "./SuggestionLoadingScreen";
import { useApiErrorMessage } from "@/core/api";
import { retargetHyperparams } from "../../utils/hyperparams";

export interface DuplicateModelState {
  duplicateFrom: {
    epochs: number;
    trainingType: ProjectTypeEnum;
    backend: ModelBackendEnum;
    annotationsUsed: ProjectTypeEnum[];
    labels: ProjectLabel[];
    outputs: ModelOutputTypeEnum[];
    region: ModelRegionEnum;
    datasetSplit: DatasetSplit;
    augmentations: AppliedAugmentation[];
    preprocessings: AppliedAugmentation[];
    customHyperparams: string;
    taskIds?: number[];
    taskPreviews?: { id: number; thumbnailUrl: string }[];
    /** Source model's dataset version — lets the new model reuse the exact same version (no duplication) when taskIds are unchanged. */
    datasetVersionId?: number | null;
    useGroups?: boolean;
  };
}

/**
 * Dataset error when none of the selected images carry the type's annotations.
 * A switch rather than a key map so each plural call is visible to i18next-cli.
 */
const missingAnnotationsMessage = (
  t: TFunction<["model", "common"]>,
  trainingType: ProjectTypeEnum,
  labeled: number,
  count: number,
): string => {
  switch (trainingType) {
    case ProjectTypeEnum.CLASSIFICATION:
      return t("model:newPage.errors.missingAnnotations.CLASSIFICATION", {
        labeled,
        count,
      });
    case ProjectTypeEnum.DETECTION:
      return t("model:newPage.errors.missingAnnotations.DETECTION", {
        labeled,
        count,
      });
    case ProjectTypeEnum.SEGMENTATION:
      return t("model:newPage.errors.missingAnnotations.SEGMENTATION", {
        labeled,
        count,
      });
  }
};

const ModelNewPageInner = () => {
  const { t } = useTranslation(["model", "common"]);
  const apiErrorMessage = useApiErrorMessage();
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateState = (location.state as DuplicateModelState | null)
    ?.duplicateFrom;

  const [activeProject] = useActiveProject();
  const [createModel] = useCreateModelMutation();
  const {
    isExhausted: isQuotaExhausted,
    label: quotaLabel,
    message: quotaMessage,
    blockedTitle: quotaBlockedTitle,
  } = useTrainingQuota();
  const { data: existingModels } = useGetModelsQuery(
    { projectId: activeProject?.id as number },
    { skip: !activeProject },
  );
  // Needed to map suggested label ids onto Label objects; shares the RTK
  // Query cache entry with SourceImagesSettings.
  const { data: projectLabels } = useGetProjectLabelsQuery(
    { projectId: activeProject?.id as number },
    { skip: !activeProject },
  );
  // Training is Ultra Vision-only. The backend field stays in the payload (and
  // the enum in the DB) so historic models still load, but new models always
  // train with Ultra Vision.
  const backend = ModelBackendEnum.ULTRA_VISION;
  // Duplicates of legacy-backend models can't reuse backend-specific settings:
  // their hyperparams use a different vocabulary, so those fall back to the
  // Ultra Vision defaults.
  const isLegacyDuplicate =
    duplicateState !== undefined &&
    duplicateState.backend !== ModelBackendEnum.ULTRA_VISION;
  const duplicateTrainingType =
    duplicateState?.trainingType === ProjectTypeEnum.CLASSIFICATION
      ? undefined
      : duplicateState?.trainingType;

  const [name, setName] = useState("");
  const [epochs, setEpochs] = useState<number | null>(
    duplicateState?.epochs ?? 100,
  );
  // RVC2/RVC3 stay opt-in rather than default: most fleets are on RVC4, and
  // every extra target is another conversion job per training run. A
  // duplicate's outputs carry over, minus the targets its type can't convert
  // to (segmentation is RVC4-only, and a legacy segmentation model may have
  // had more).
  const [outputs, setOutputs] = useState<ModelOutputTypeEnum[]>(() =>
    duplicateState?.outputs?.length
      ? duplicateState.outputs.filter((output) =>
          isConversionTargetSupported(
            ModelBackendEnum.ULTRA_VISION,
            duplicateTrainingType ?? ProjectTypeEnum.DETECTION,
            output,
          ),
        )
      : [ModelOutputTypeEnum.RAW, ModelOutputTypeEnum.RVC4],
  );
  const [region, setRegion] = useState<ModelRegionEnum>(
    duplicateState?.region ?? ModelRegionEnum.EUROPE_WEST4,
  );
  // Seeds the conversions dispatched when training completes. Precision is a
  // per-conversion choice afterwards, so this is a default rather than a
  // commitment. FP16 stays the default — INT8 needs calibration and is the
  // opt-in.
  const [quantization, setQuantization] = useState<ModelQuantizationEnum>(
    ModelQuantizationEnum.FP16,
  );
  // A duplicate starts from the source model's classes, minus any label that
  // has been archived since: a new model can't be built on one.
  const archivedDuplicateLabels = useMemo(
    () => duplicateState?.labels.filter((label) => label.deletedAt) ?? [],
    [duplicateState],
  );
  const [activeLabels, setActiveLabels] = useState<ProjectLabel[]>(
    () => duplicateState?.labels.filter((label) => !label.deletedAt) ?? [],
  );
  const [datasetSplit, setDatasetSplit] = useState<DatasetSplit>(
    duplicateState?.datasetSplit ?? { train: 70, validation: 20, test: 10 },
  );
  // Preprocessing + augmentation UI is hidden; values come from a duplicated
  // model's payload (when duplicating) or default to empty.
  const augmentations: AppliedAugmentation[] =
    duplicateState?.augmentations ?? [];
  const preprocessings: AppliedAugmentation[] =
    duplicateState?.preprocessings ?? [];
  const [trainingType, setTrainingType] = useState<ProjectTypeEnum>(
    duplicateTrainingType ?? ProjectTypeEnum.DETECTION,
  );
  const [annotationsUsed, setAnnotationsUsed] = useState<ProjectTypeEnum[]>(
    duplicateTrainingType && duplicateState?.annotationsUsed
      ? duplicateState.annotationsUsed
      : [ProjectTypeEnum.DETECTION, ProjectTypeEnum.SEGMENTATION],
  );
  const [customHyperparams, setCustomHyperparams] = useState(() => {
    if (!isLegacyDuplicate && duplicateState?.customHyperparams !== undefined) {
      return duplicateState.customHyperparams;
    }
    // Default to the High Accuracy preset.
    const preset = getHyperparamsPresets(
      duplicateTrainingType ?? ProjectTypeEnum.DETECTION,
    ).find((p) => p.id === "high-accuracy");
    return preset ? JSON.stringify(preset.config, null, 2) : "";
  });
  const [hyperparamsError, setHyperparamsError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [epochsError, setEpochsError] = useState<string | null>(null);
  const [datasetError, setDatasetError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [checkDataset] = useDatasetStatsMutation();
  const didPrefillName = useRef(false);

  // Task selection state — modal-controlled
  const [selectionDialogOpen, setSelectionDialogOpen] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<number[]>(
    duplicateState?.taskIds ?? [],
  );
  const [selectedTaskPreviews, setSelectedTaskPreviews] = useState<
    { id: number; thumbnailUrl: string }[]
  >(duplicateState?.taskPreviews ?? []);
  // Source model's dataset version (only set when duplicating). The backend
  // reuses this version if taskIds are unchanged, or appends a new version
  // under the same dataset if they've been edited.
  const sourceDatasetVersionId = duplicateState?.datasetVersionId ?? undefined;
  const [useGroups, setUseGroups] = useState<boolean>(
    duplicateState?.useGroups ?? true,
  );

  // When duplicating, we receive taskIds but no thumbnail URLs. Fetch them
  // so the Source Images card can render the preview row.
  const needsPreviewFetch =
    selectedTaskIds.length > 0 && selectedTaskPreviews.length === 0;
  const previewIds = selectedTaskIds.slice(0, MAX_VISIBLE_THUMBNAILS);
  const { data: previewTasksData } = useGetTasksQuery(
    {
      projectId: activeProject?.id as number,
      limit: previewIds.length || 1,
      ids: previewIds.join(","),
    },
    { skip: !activeProject?.id || !needsPreviewFetch },
  );

  useEffect(() => {
    if (!needsPreviewFetch || !previewTasksData) return;
    setSelectedTaskPreviews(
      previewTasksData.data.map((t) => ({
        id: t.id,
        thumbnailUrl: t.thumbnailUrl,
      })),
    );
  }, [needsPreviewFetch, previewTasksData]);

  // First-load default: behave as if the user opened the dialog and clicked
  // "select all". Pre-populate the explicit task list + a thumbnail row so the
  // Source Images card renders previews + overflow instead of "All images".
  // Skipped when duplicating (taskIds already provided) or after any user
  // interaction with the dialog.
  const userPickedTasks = useRef(Boolean(duplicateState));
  const shouldPrefillTasks =
    !userPickedTasks.current && selectedTaskIds.length === 0;
  const { data: defaultTaskIdsData } = useGetTaskIdsQuery(
    {
      projectId: activeProject?.id as number,
      annotated: "true",
      sortOrder: "desc",
    },
    { skip: !activeProject?.id || !shouldPrefillTasks },
  );
  const { data: defaultTasksData } = useGetTasksQuery(
    {
      projectId: activeProject?.id as number,
      page: 1,
      limit: 15,
      annotated: "true",
      sortOrder: "desc",
    },
    { skip: !activeProject?.id || !shouldPrefillTasks },
  );

  useEffect(() => {
    if (userPickedTasks.current) return;
    if (!defaultTaskIdsData || !defaultTasksData) return;
    userPickedTasks.current = true;
    setSelectedTaskIds(defaultTaskIdsData.ids);
    setSelectedTaskPreviews(
      defaultTasksData.data.map((t) => ({
        id: t.id,
        thumbnailUrl: t.thumbnailUrl,
      })),
    );
  }, [defaultTaskIdsData, defaultTasksData]);

  // Clear location state after reading to prevent re-prefill on refresh
  useEffect(() => {
    if (location.state?.duplicateFrom) {
      window.history.replaceState({}, "");
    }
  }, []);

  useEffect(() => {
    if (didPrefillName.current || !existingModels) return;
    didPrefillName.current = true;
    const next = existingModels.length + 1;
    setName(t("newPage.defaultName", { number: String(next).padStart(2, "0") }));
  }, [existingModels, t]);

  // Debounced dataset validation — fires whenever the user changes task selection
  // or training type. Disables Save/Train if no annotated images match the type.
  useEffect(() => {
    if (!activeProject?.id) return;
    const timer = setTimeout(() => {
      checkDataset({
        projectId: activeProject.id,
        taskIds: selectedTaskIds,
        trainingType,
        annotationsUsed,
      })
        .unwrap()
        .then((result) => {
          if (!result.valid) {
            const msg =
              result.totalCandidateCount === 0
                ? t("newPage.errors.noAnnotatedImages")
                : missingAnnotationsMessage(
                    t,
                    trainingType,
                    result.labeledCount,
                    result.totalCandidateCount,
                  );
            setDatasetError(msg);
          } else {
            setDatasetError(null);
          }
        })
        .catch(() => setDatasetError(null));
    }, 300);
    return () => clearTimeout(timer);
    // checkDataset is a stable mutation trigger — omitted from deps intentionally
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTaskIds, trainingType, annotationsUsed, activeProject?.id]);

  // Best-effort parse of the hyperparams JSON for the AI-suggestion flow —
  // invalid/partial JSON degrades to {} instead of blocking the dialog.
  const currentHyperparamsObject = useMemo((): Record<string, unknown> => {
    try {
      const parsed = JSON.parse(customHyperparams);
      return typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }, [customHyperparams]);

  const suggestionContext: SuggestHyperparamsRequest & { projectId: number } = {
    projectId: activeProject?.id as number,
    backend,
    taskIds: selectedTaskIds,
    labelIds: activeLabels.map((label) => label.id),
    trainingType,
    annotationsUsed,
    epochs,
    currentHyperparams: currentHyperparamsObject,
    splitTrain: datasetSplit.train,
    splitValidate: datasetSplit.validation,
    splitTest: datasetSplit.test,
    quantization,
    outputTypes: outputs,
    useGroups,
  };

  // The two model families take different hyperparameter names and sizes, and
  // segmentation can't convert to RVC2/RVC3, so a type change carries the form
  // over rather than leaving settings the new type would reject.
  const handleTrainingTypeChange = (next: ProjectTypeEnum) => {
    if (next === trainingType) return;
    setCustomHyperparams((current) =>
      retargetHyperparams(current, trainingType, next, backend),
    );
    setHyperparamsError(null);
    setOutputs((current) =>
      current.filter((output) =>
        isConversionTargetSupported(backend, next, output),
      ),
    );
    setTrainingType(next);
  };

  const handleApplySuggestion = ({
    epochs: suggestedEpochs,
    split,
    params,
    labelIds,
  }: AppliedAutoSuggestion) => {
    setEpochs(suggestedEpochs);
    setEpochsError(null);
    setDatasetSplit(split);
    // Suggested values win; keys the suggestion didn't touch are preserved.
    const merged: Record<string, unknown> = {
      ...currentHyperparamsObject,
      ...params,
    };
    setCustomHyperparams(JSON.stringify(merged, null, 2));
    setHyperparamsError(null);
    if (labelIds.length > 0 && projectLabels) {
      const suggestedLabels = projectLabels.filter((label) =>
        labelIds.includes(label.id),
      );
      if (suggestedLabels.length > 0) setActiveLabels(suggestedLabels);
    }
  };

  // Live snapshot so Retry re-reads the current form state, not the state
  // captured when the hook mounted.
  const suggestionContextRef = useRef(suggestionContext);
  suggestionContextRef.current = suggestionContext;
  const {
    status: suggestionStatus,
    errorMessage: suggestionError,
    aiSuggestion,
    skip: skipSuggestion,
    retry: retrySuggestion,
  } = useAutoSuggestion({
    enabled: !duplicateState,
    projectId: activeProject?.id,
    getContext: () => suggestionContextRef.current,
    onApply: handleApplySuggestion,
  });

  const parseHyperparams = (): Record<string, unknown> | undefined => {
    if (!customHyperparams.trim()) return {};
    try {
      const parsed = JSON.parse(customHyperparams);
      if (
        typeof parsed !== "object" ||
        Array.isArray(parsed) ||
        parsed === null
      ) {
        setHyperparamsError(t("hyperparams.errors.notObject"));
        return undefined;
      }
      setHyperparamsError(null);
      return parsed;
    } catch {
      setHyperparamsError(t("newPage.errors.invalidJson"));
      return undefined;
    }
  };

  const saveModel = async (train = false) => {
    if (datasetError) return;
    if (useGroups && trainingType !== ProjectTypeEnum.DETECTION) return;
    setSaveError(null);
    const trimmedName = name.trim();
    const nextNameError = trimmedName
      ? null
      : t("newPage.errors.versionNameRequired");
    const nextEpochsError =
      epochs !== null && epochs > 0 ? null : t("newPage.errors.epochsPositive");
    setNameError(nextNameError);
    setEpochsError(nextEpochsError);
    if (nextNameError || nextEpochsError) return;

    const parsedHyperparams = parseHyperparams();
    if (parsedHyperparams === undefined) return;

    const normalAugs = augmentations.filter((a) => !a.duplicateImage);
    const duplicateAugs = augmentations.filter((a) => a.duplicateImage);

    const allPreprocessings = [
      ...preprocessings.map((p) => ({
        type: p.type,
        params: p.params,
        keepOriginal: false,
      })),
      ...duplicateAugs.map((a) => {
        const { p: _p, ...paramsWithoutP } = a.params;
        return {
          type: a.type,
          params: paramsWithoutP,
          keepOriginal: true,
        };
      }),
    ];

    try {
      const newModel = await createModel({
        epochs: epochs!,
        labelIds: activeLabels?.map((label) => label.id) || [],
        taskIds: selectedTaskIds,
        ...(sourceDatasetVersionId != null && { sourceDatasetVersionId }),
        name,
        projectId: activeProject?.id as number,
        splitTest: datasetSplit.test,
        splitTrain: datasetSplit.train,
        splitValidate: datasetSplit.validation,
        outputTypes: outputs,
        backend,
        region,
        quantization,
        trainingType,
        annotationsUsed,
        augmentations: normalAugs.map((a) => ({
          type: a.type,
          params: a.params,
        })),
        preprocessings: allPreprocessings,
        customHyperparams: parsedHyperparams,
        aiSuggestion,
        train,
        useGroups,
      }).unwrap();
      navigate(`/projects/${activeProject?.id}/models/${newModel.id}`);
    } catch (err: unknown) {
      setSaveError(apiErrorMessage(err, t("newPage.errors.saveFailed")));
    }
  };

  if (suggestionStatus === "checking" || suggestionStatus === "loading") {
    return (
      <ModelLayout>
        <SuggestionLoadingScreen onSkip={skipSuggestion} />
      </ModelLayout>
    );
  }

  return (
    <ModelLayout>
      <div className="flex flex-col gap-6 pb-4">
        <div className="flex flex-col gap-2">
          <h2 className="text-base font-bold leading-6 text-black/90">
            {t("newPage.title")}
          </h2>
          <p className="text-sm leading-5 text-black/60">
            {t("newPage.description")}
          </p>
        </div>
        {suggestionStatus === "applied" && aiSuggestion && (
          <AiSuggestionNotes {...aiSuggestion} />
        )}
        {suggestionStatus === "failed" && (
          <Alert className="border-amber-200 bg-amber-50 py-2 text-amber-800">
            <AlertTriangle className="size-4" />
            <AlertTitle className="text-sm font-medium">
              {t("newPage.suggestionFailed")}
            </AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-2">
              {suggestionError}
              <Button variant="outline" size="sm" onClick={retrySuggestion}>
                {t("common:actions.retry")}
              </Button>
            </AlertDescription>
          </Alert>
        )}
        <div className="flex flex-row items-start gap-2">
          <Label
            htmlFor="versionName"
            className="h-9 shrink-0 items-center text-xs font-normal text-black/60"
          >
            {t("fields.versionName")}
          </Label>
          <div className="flex flex-1 flex-col gap-1">
            <Input
              id="versionName"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (nameError) setNameError(null);
              }}
              aria-invalid={Boolean(nameError) || undefined}
            />
            {nameError && (
              <span className="text-xs text-red-600">{nameError}</span>
            )}
          </div>
          <Label
            htmlFor="epochs"
            className="h-9 shrink-0 items-center text-xs font-normal text-black/60"
          >
            {t("fields.epochs")}
          </Label>
          <div className="flex flex-1 flex-col gap-1">
            <NumberInput
              id="epochs"
              min={1}
              value={epochs}
              onValueChange={(v) => {
                setEpochs(v);
                if (epochsError) setEpochsError(null);
              }}
              error={Boolean(epochsError)}
            />
            {epochsError && (
              <span className="text-xs text-red-600">{epochsError}</span>
            )}
          </div>
        </div>
        <ModelTypeSettings
          trainingType={trainingType}
          annotationsUsed={annotationsUsed}
          onTrainingTypeChange={handleTrainingTypeChange}
          onAnnotationsUsedChange={setAnnotationsUsed}
        />
        {archivedDuplicateLabels.length > 0 && (
          <p className="rounded-lg bg-black/[0.04] px-3 py-2 text-xs text-foreground/70">
            {t("newPage.archivedLabelsDropped", {
              count: archivedDuplicateLabels.length,
              names: archivedDuplicateLabels.map((l) => l.name).join(", "),
            })}
          </p>
        )}
        <SourceImagesSettings
          setActiveLabels={setActiveLabels}
          activeLabels={activeLabels}
          selectedTaskIds={selectedTaskIds}
          selectedTaskPreviews={selectedTaskPreviews}
          onEditSelection={() => setSelectionDialogOpen(true)}
          datasetError={datasetError}
          useGroups={useGroups}
          setUseGroups={setUseGroups}
          trainingType={trainingType}
        />
        <DatasetSplitSettings
          split={datasetSplit}
          onChange={setDatasetSplit}
          customTotal={
            selectedTaskIds.length > 0 ? selectedTaskIds.length : undefined
          }
        />
        <AdvancedSettings
          backend={backend}
          trainingType={trainingType}
          outputs={outputs}
          onOutputsChange={setOutputs}
          quantization={quantization}
          onQuantizationChange={setQuantization}
          region={region}
          onRegionChange={setRegion}
          customHyperparams={customHyperparams}
          onCustomHyperparamsChange={setCustomHyperparams}
          hyperparamsError={hyperparamsError}
          onHyperparamsErrorChange={setHyperparamsError}
        />

        {saveError && <span className="text-xs text-red-600">{saveError}</span>}
        {quotaMessage && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            <TriangleAlert className="mt-px size-4 shrink-0" aria-hidden />
            <span className="text-pretty">{quotaMessage}</span>
          </div>
        )}
        <div className="flex flex-row items-center justify-end gap-2">
          {quotaLabel && (
            <span
              className={
                isQuotaExhausted
                  ? "mr-auto text-xs text-red-600"
                  : "mr-auto text-xs text-muted-foreground"
              }
            >
              {quotaLabel}
            </span>
          )}
          {/* Save stays available at the limit — the config can be trained next month. */}
          <Button onClick={() => saveModel()} disabled={Boolean(datasetError)}>
            {t("common:actions.save")}
          </Button>
          {/* title sits on the wrapper: a disabled button does not show its own. */}
          <span title={quotaBlockedTitle} className="flex">
            <Button
              onClick={() => saveModel(true)}
              disabled={Boolean(datasetError) || isQuotaExhausted}
            >
              {t("newPage.saveAndTrain")}
            </Button>
          </span>
        </div>
      </div>

      <TaskSelectionDialog
        open={selectionDialogOpen}
        onOpenChange={setSelectionDialogOpen}
        initialSelectedIds={selectedTaskIds}
        onSave={({ taskIds, previews }) => {
          userPickedTasks.current = true;
          setSelectedTaskIds(taskIds);
          setSelectedTaskPreviews(previews);
        }}
      />
    </ModelLayout>
  );
};

export const ModelNewPage = () => {
  const location = useLocation();
  return <ModelNewPageInner key={location.key} />;
};
